import type { BaseTheme } from "common/BaseTheme";
import { Color, randomTheme } from "common/BaseTheme";
import { DeviceFormFactor } from "common/render/form-factor";
import { assertNever } from "common/Support";
import * as deepmerge from "deepmerge";
import { isOSThemeDark } from "sharedUI/lib/color-theme-utils";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import type { CoreTheme } from "sharedUI/styles";
import tinycolor from "tinycolor2";
import { isPlayer } from "../../../routes";
import type { RuntimeTheme as OriginalRuntimeTheme } from "./core-themes/runtime-theme";
import { defaultRuntimeTheme } from "./core-themes/runtime-theme";
import type { WireAppTheme as OriginalWireAppTheme } from "./core-themes/wireapp-theme";
import { defaultWireAppTheme, highlightBackgroundWireAppOverlay } from "./core-themes/wireapp-theme";
import type { OverlayTheme } from "./overlay-theme";
import { androidTabletOverlay, desktopOverlay, tabletOverlay } from "./tablet-overlay";
import { androidOverlay } from "./theme-overlays/android-theme-overlay";
import { colorDarkOverlay, colorOverlay } from "./theme-overlays/color-theme-overlay";
import { darkOverlay } from "./theme-overlays/dark-theme-overlays/dark-app-theme";
import { darkWireThemeOverlay } from "./theme-overlays/dark-theme-overlays/dark-wire-theme";
import { pureBlackOverlay } from "./theme-overlays/pure-black-theme-overlay";
import { pureWhiteOverlay } from "./theme-overlays/pure-white-theme-overlay";
export { pageMarginMedium } from "./core-themes/runtime-theme";

export const mobileSystemFontFamily = "-apple-system,BlinkMacSystemFont,Roboto,sans-serif";
export const pagesFontFamily = "Inter,-apple-system,BlinkMacSystemFont,Roboto,sans-serif";

export const primaryColors = Object.keys(Color).map(name => ({ name, value: (Color as any)[name] }));
const types = ["modern", "editorial", "future", "system"];
export const typographyTypes = types.map(type => ({
    name: type.slice(0, 1).toUpperCase() + type.slice(1),
    value: type,
}));

export type RuntimeTheme = OriginalRuntimeTheme;
export type WireAppTheme = OriginalWireAppTheme;

let currentThemeCache: RuntimeTheme | WireAppTheme | undefined;
let initialsFontColorCache: string | undefined;
let initialsBackgroundCache: string | undefined;

export function initialsFontColor(theme: RuntimeTheme): string {
    // must use any here or we break typing
    if (theme === currentThemeCache && initialsFontColorCache !== undefined) {
        return initialsFontColorCache;
    }

    const result = tinycolor(theme.primaryAccentColor).darken(theme.listItem.textDarkenAmount).toHexString();
    currentThemeCache = theme;
    initialsFontColorCache = result;
    return result;
}

export function initialsBackground(theme: RuntimeTheme): string {
    // must use any here or we break typing
    if (theme === currentThemeCache && initialsBackgroundCache !== undefined) {
        return initialsBackgroundCache;
    }

    const result = tinycolor(theme.primaryAccentColor).desaturate().setAlpha(0.12).toRgbString();
    currentThemeCache = theme;
    initialsBackgroundCache = result;
    return result;
}

export type ThemePlatform = "iOS" | "Android";

export function getRuntimeThemeForPlatform(
    appTheme?: BaseTheme,
    platform?: ThemePlatform,
    deviceFormFactor?: DeviceFormFactor,
    darkMode?: boolean
): RuntimeTheme {
    currentThemeCache = undefined;
    initialsFontColorCache = undefined;
    initialsBackgroundCache = undefined;

    if (platform === undefined) {
        platform = "iOS";
    }

    const adaptive = appTheme?.themeIsAdaptive === true && isPlayer();
    const isOSDark: boolean = darkMode ?? isOSThemeDark();

    let overlay: OverlayTheme | undefined;
    if (appTheme !== undefined) {
        switch (appTheme.themeOverlay) {
            case "dark":
                overlay = darkOverlay;
                if (!isOSDark && adaptive) {
                    overlay = undefined;
                }
                break;
            case "pureblack":
                overlay = pureBlackOverlay;
                if (!isOSDark && adaptive) {
                    overlay = pureWhiteOverlay;
                }
                break;
            case "color":
                overlay = colorOverlay(appTheme);
                if (isOSDark && adaptive) {
                    overlay = colorDarkOverlay(appTheme);
                }
                break;
            case "colorDark":
                overlay = colorDarkOverlay(appTheme);
                if (!isOSDark && adaptive) {
                    overlay = colorOverlay(appTheme);
                }
                break;
            case "purewhite":
                overlay = pureWhiteOverlay;
                if (isOSDark && adaptive) {
                    overlay = pureBlackOverlay;
                }
                break;
            default:
                if (isOSDark && adaptive) {
                    overlay = darkOverlay;
                }
        }
    }

    const baseTheme = randomTheme("none");
    if (appTheme === undefined) {
        appTheme = baseTheme;
    }

    const deepMergedThemes = deepmerge.all([baseTheme, appTheme, defaultRuntimeTheme]) as RuntimeTheme;
    const overlays = [overlay];
    if (deviceFormFactor === DeviceFormFactor.Tablet) {
        if (platform === "Android") {
            overlays.push(androidTabletOverlay);
        } else {
            overlays.push(tabletOverlay);
        }
    } else if (deviceFormFactor === DeviceFormFactor.Desktop) {
        overlays.push(desktopOverlay);
    }

    const resultingTheme = getThemeForPlatform(platform, deepMergedThemes, overlays);

    setMetaAppleStatusBar(resultingTheme);
    setMetaThemeColor(resultingTheme);

    return resultingTheme;
}

function setMetaAppleStatusBar(theme: RuntimeTheme) {
    if (theme.themeOverlay === "dark" || theme.themeOverlay === "pureblack") {
        const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
        if (statusBarMeta !== null) {
            statusBarMeta.setAttribute("content", "black");
        }
    } else if (theme.themeOverlay === "color" || theme.themeOverlay === "colorDark") {
        const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
        if (statusBarMeta !== null) {
            statusBarMeta.setAttribute("content", "black-translucent");
        }
    }
}

// This meta tag is appended in the server's request, for some reason.
// See functions/src/functions/play.ts
function setMetaThemeColor(theme: CoreTheme) {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta !== null) {
        themeColorMeta.setAttribute("content", theme.OSstatusBar);
    }
}

function getThemeForPlatform(
    platform: ThemePlatform,
    originalTheme: OriginalRuntimeTheme,
    overlays: (OverlayTheme | undefined)[]
): RuntimeTheme {
    switch (platform) {
        case "iOS":
            return mergeTheme(originalTheme, [...overlays]);
        case "Android":
            return mergeTheme(originalTheme, [androidOverlay, ...overlays]);
        default:
            assertNever(platform);
    }
}

export function getWireAppThemeForPlatform(
    appTheme?: BaseTheme,
    platform?: ThemePlatform,
    _darkMode?: boolean
): WireAppTheme {
    currentThemeCache = undefined;
    initialsFontColorCache = undefined;
    initialsBackgroundCache = undefined;

    if (platform === undefined) {
        platform = "iOS";
    }

    const adaptive = false;
    const isOSDark: boolean = false;

    let overlay: OverlayTheme | undefined;
    if (appTheme !== undefined) {
        if (isOSDark && adaptive) {
            overlay = darkWireThemeOverlay;
        }
    }

    const baseTheme = randomTheme("none");
    if (appTheme === undefined) {
        appTheme = baseTheme;
    }

    const overlays = [overlay];
    if (appTheme?.pageBackground === "Highlight") {
        overlays.push(highlightBackgroundWireAppOverlay);
    }

    const deepMergedThemes = deepmerge.all([baseTheme, appTheme, defaultWireAppTheme]) as WireAppTheme;
    return mergeTheme(deepMergedThemes, [...overlays]);
}

export function isDarkTheme(theme: { themeOverlay?: string }): boolean {
    return theme.themeOverlay === "dark" || theme.themeOverlay === "pureblack" || theme.themeOverlay === "colorDark";
}
